import { deduplicateArray, nullishFilter, filterObjectByEnum, objectFilter, flattenArray, errorSuffix, objectMap, errorSuffixNurse, MINUTE } from '@caresend/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import update from 'immutability-helper';
import { ProgressStepStatus } from '../components/ui/ProgressSteps/model.js';
import { reportException } from '../functions/sentry.js';
import { toastError } from '../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../components/ui/Icon.vue.js';
import '../functions/configuration/icons.js';
import '../database/algoliaConfig/indices.js';
import '@caresend/types';
import 'mobile-device-detect';
import '../functions/tracking/index.js';
import { keepParams, keepQuery } from '../router/helpers/builders.js';
import { getRouter } from '../router/helpers/initRouter.js';
import { getIsDebugMode } from '../store/modules/variables/helpers.js';

dayjs.extend(relativeTime);
const addAssociatedIDs = (baseStep, id, parentID, parentRouteName, parentAssociatedParam, additionalParams) => ({
    ...baseStep,
    associatedID: id,
    ...(parentID ? { parentAssociatedID: parentID } : undefined),
    ...(parentRouteName ? { parentRouteName } : undefined),
    ...(parentAssociatedParam ? { parentAssociatedParam } : undefined),
    ...(additionalParams ? { additionalParams } : undefined),
});
/**
 * Returns a flattened list of all the possible steps.
 * This can be used to easily find a specific step in the hierarchy, without
 * needing to work with nested data.
 */
const getFlattenedFlowSteps = (stepMap) => {
    const stepsArray = [
        ...stepMap.values(),
    ];
    const flatSteps = [];
    const recursiveFlattenSteps = (steps) => {
        steps.forEach((step) => {
            flatSteps.push(step);
            if (step.subSteps) {
                const subStepsMapArray = Object.values(step.subSteps);
                const subStepsArray = flattenArray(subStepsMapArray.map((subStepMap) => [...subStepMap.values()]));
                flatSteps.concat(subStepsArray);
                recursiveFlattenSteps(subStepsArray);
            }
        });
    };
    recursiveFlattenSteps(stepsArray);
    return flatSteps;
};
/**
 * Given a routeName, get the index of its highest parent in the hierarchy.
 * If the routeName is a root step, return the index of the root step.
 */
const getRootLevelStepIndex = (routeName, stepMap) => {
    const steps = [...stepMap.values()];
    const flatSteps = getFlattenedFlowSteps(stepMap);
    const foundStep = flatSteps.find((step) => step.routeName === routeName);
    if (!foundStep)
        return -1;
    const { parentRouteName } = foundStep;
    if (!parentRouteName)
        return steps.indexOf(foundStep);
    return getRootLevelStepIndex(parentRouteName, stepMap);
};
/** Given a routeName and associated ID, find a specific step */
const getFlowStepByRouteNameAndAssociatedID = (routeName, associatedID, flatSteps) => {
    const parentStep = flatSteps.find((step) => (step.routeName === routeName
        && step.associatedID === associatedID));
    return parentStep !== null && parentStep !== void 0 ? parentStep : null;
};
/** Given a step, return the parent step, if there is one. */
const getParentFlowStep = (step, flatSteps) => {
    const { parentAssociatedID, parentRouteName } = step;
    if (!parentRouteName || !parentAssociatedID)
        return null;
    return getFlowStepByRouteNameAndAssociatedID(parentRouteName, parentAssociatedID, flatSteps);
};
/**
 * Given a route, return the appropriate step. This is typically used to get
 * the current step, but is also used in conjunction with `prevRoute` to get
 * the step for the route the user visited last.
 */
const getFlowStepFromRoute = (flowConfig, route, stepMap) => {
    var _a;
    const routeName = route.name;
    const { params } = route;
    const rootID = params === null || params === void 0 ? void 0 : params[flowConfig.rootParam];
    if (!routeName || !rootID)
        return null;
    const flatSteps = getFlattenedFlowSteps(stepMap);
    const matchingSteps = flatSteps.filter((step) => step.routeName === routeName);
    return (_a = matchingSteps.find((step) => { var _a; return step.associatedID === ((_a = route.params) === null || _a === void 0 ? void 0 : _a[step.associatedParam]); })) !== null && _a !== void 0 ? _a : null;
};
/**
 * Get the current step map in the hierarchy.
 *
 * - If the user is on a root step, this is the root step map.
 * - Otherwise, this is the subStep map of the current hierarchy. i.e., In the
 *   patient visit flow, if the user is on a procedure step, it is only the
 *   steps specific to that procedure.
 */
const getCurrentFlowSubSteps = ({ flowConfig, route, stepMap, }) => {
    var _a, _b, _c;
    const rootID = (_a = route.params) === null || _a === void 0 ? void 0 : _a[flowConfig.rootParam];
    const currentStep = getFlowStepFromRoute(flowConfig, route, stepMap);
    if (!currentStep || !rootID)
        return null;
    if (!currentStep.parentRouteName) {
        return stepMap;
    }
    const { associatedID, parentAssociatedID, parentAssociatedParam, parentRouteName, } = currentStep;
    if (!parentAssociatedParam || !parentRouteName || !parentAssociatedID)
        return null;
    const flatSteps = getFlattenedFlowSteps(stepMap);
    const parentStep = getFlowStepByRouteNameAndAssociatedID(parentRouteName, parentAssociatedID, flatSteps);
    return (_c = (_b = parentStep === null || parentStep === void 0 ? void 0 : parentStep.subSteps) === null || _b === void 0 ? void 0 : _b[associatedID]) !== null && _c !== void 0 ? _c : null;
};
/**
 * Example: Given the following flowConfig, step and rootID:
 *
 * ```
 * flowConfig: { rootParam: 'waypointID' }
 * step: {
 *   associatedParam: 'procedureID',
 *   associatedID: 'x6k1kuvtebl33c3a7e'
 *   parentAssociatedParam: 'waypointActionID',
 *   parentAssociatedID: 'hjud361ma16l33ceaqt',
 *   additionalParams: {
 *     productID: 'NLYt6j1iVXXstgR3T4tp',
 *   },
 * },
 * rootID: 'qsgnvrx3k8fl2zcpre2'
 * ```
 *
 * The following is returned, depending on param passed:
 *
 * `param: 'waypointID' => 'qsgnvrx3k8fl2zcpre2'`
 *
 * `param: 'waypointActionID' => 'hjud361ma16l33ceaqt'`
 *
 * `param: 'procedureID' => 'x6k1kuvtebl33c3a7e'`
 *
 * `param: 'productID' => 'NLYt6j1iVXXstgR3T4tp'`
 */
const getIdForParam = (flowConfig, param, step, rootID) => {
    var _a, _b, _c;
    switch (param) {
        case flowConfig.rootParam:
            return rootID;
        case step.parentAssociatedParam:
            return (_a = step.parentAssociatedID) !== null && _a !== void 0 ? _a : null;
        case step.associatedParam:
            return step.associatedID;
        default:
            return (_c = (_b = step.additionalParams) === null || _b === void 0 ? void 0 : _b[param]) !== null && _c !== void 0 ? _c : null;
    }
};
/**
 * Generates a route from available information (rootParam, associatedParam,
 * parentAssociatedParam) In a deeply nested structure there could be params
 * missing in the object returned from this function, but any params that are
 * already in the URL will remain upon navigation.
 */
const getRouteFromFlowStep = (flowConfig, step, rootID) => {
    var _a;
    const paramsToInclude = deduplicateArray([
        flowConfig.rootParam,
        step.parentAssociatedParam,
        step.associatedParam,
        ...Object.keys((_a = step.additionalParams) !== null && _a !== void 0 ? _a : {}),
    ]).filter(nullishFilter);
    const params = paramsToInclude.reduce((currentParams, param) => ({
        ...currentParams,
        [param]: getIdForParam(flowConfig, param, step, rootID),
    }), {});
    return {
        name: step.routeName,
        params,
    };
};
/**
 * Gets the steps for navigation to `back` or `next`.
 *
 * - The very first root step has no `back` step.
 *   - The `SomeFlow.vue` file can handle this case by returning to a particular
 *     page if this function returns null.
 * - The very last root step has no `next` step.
 *   - The `SomeFlow.vue` file can handle this case by returning to a particular
 *     page if this function returns null.
 * - Otherwise, the `next` step returns as follows:
 *   - The next step in current set of (sub)steps, or, if there is none,
 *   - The parent step in the hierarchy,
 * - Otherwise, the `back` step returns as follows:
 *   - The last step in current set of (sub)steps, or, if there is none,
 *   - The parent step in the hierarchy
 */
const getBackOrNextFlowStep = ({ direction, flowConfig, route, stepMap, }) => {
    var _a, _b, _c;
    const currentStep = getFlowStepFromRoute(flowConfig, route, stepMap);
    const currentSubSteps = getCurrentFlowSubSteps({ flowConfig, route, stepMap });
    const rootID = (_a = route.params) === null || _a === void 0 ? void 0 : _a[flowConfig.rootParam];
    if (!currentStep || !currentSubSteps || !rootID)
        return null;
    const flatSteps = getFlattenedFlowSteps(stepMap);
    const routeNames = [...currentSubSteps.keys()];
    const currentIndex = routeNames.indexOf(currentStep.routeName);
    switch (direction) {
        // - The next step in current set of (sub)steps, or
        // - The parent step in the hierarchy, or
        // - null (user is on the last step of root steps)
        case 'next': {
            const nextRouteName = routeNames[currentIndex + 1];
            // No next step in current level of hierarchy, look for parent step
            // or return null (last root step)
            if (!nextRouteName) {
                return getParentFlowStep(currentStep, flatSteps);
            }
            // Next step in current level of hierarchy
            return (_b = currentSubSteps.get(nextRouteName)) !== null && _b !== void 0 ? _b : null;
        }
        // - The last step in current set of (sub)steps, or
        // - The parent step in the hierarchy, or
        // - null (user is on first step)
        case 'back': {
            const prevRouteName = routeNames[currentIndex - 1];
            // No previous step in current level of hierarchy, look for parent step
            // or return null (first root step)
            if (!prevRouteName) {
                return getParentFlowStep(currentStep, flatSteps);
            }
            // Previous step in current level of hierarchy
            return (_c = currentSubSteps.get(prevRouteName)) !== null && _c !== void 0 ? _c : null;
        }
    }
};
/**
 * Gets the route associated with the steps for navigation to `back` or `next`.
 *
 * - The very first root step has no `back` step.
 *   - The `SomeFlow.vue` file can handle this case by returning to a particular
 *     page if this function returns null.
 * - The very last root step has no `next` step.
 *   - The `SomeFlow.vue` file can handle this case by returning to a particular
 *     page if this function returns null.
 * - Otherwise, the `next` step returns as follows:
 *   - The next step in current set of (sub)steps, or, if there is none,
 *   - The parent step in the hierarchy,
 * - Otherwise, the `back` step returns as follows:
 *   - The last step in current set of (sub)steps, or, if there is none,
 *   - The parent step in the hierarchy
 */
const getBackOrNextFlowStepRoute = ({ direction, flowConfig, route, stepMap, }) => {
    var _a;
    const step = getBackOrNextFlowStep({ direction, flowConfig, route, stepMap });
    if (!step)
        return null;
    const rootID = (_a = route.params) === null || _a === void 0 ? void 0 : _a[flowConfig.rootParam];
    if (!rootID)
        return null;
    return getRouteFromFlowStep(flowConfig, step, rootID);
};
const getFlowStepBackHeaderComponent = (route, flowConfig, stepMap) => {
    var _a;
    const currentStep = getFlowStepFromRoute(flowConfig, route, stepMap);
    return (_a = currentStep === null || currentStep === void 0 ? void 0 : currentStep.backHeaderComponent) !== null && _a !== void 0 ? _a : null;
};
const getFlowStepSecondaryHeaderComponent = (route, flowConfig, stepMap) => {
    var _a;
    const currentStep = getFlowStepFromRoute(flowConfig, route, stepMap);
    return (_a = currentStep === null || currentStep === void 0 ? void 0 : currentStep.secondaryHeaderComponent) !== null && _a !== void 0 ? _a : null;
};
const getFlowStepTertiaryHeaderComponent = (route, flowConfig, stepMap) => {
    var _a;
    const currentStep = getFlowStepFromRoute(flowConfig, route, stepMap);
    return (_a = currentStep === null || currentStep === void 0 ? void 0 : currentStep.tertiaryHeaderComponent) !== null && _a !== void 0 ? _a : null;
};
const getCurrentFlowStepValidationFailures = (route, { getFlowLoading, stepValidators }) => {
    var _a, _b;
    // Fail validation if the flow is loading
    const hasFlowIsLoadingGetter = !!getFlowLoading;
    if (hasFlowIsLoadingGetter && (getFlowLoading === null || getFlowLoading === void 0 ? void 0 : getFlowLoading()))
        return null;
    // Return results of validator if present. Otherwise, pass validation.
    const routeName = route.name;
    if (routeName) {
        return (_b = (_a = stepValidators === null || stepValidators === void 0 ? void 0 : stepValidators[routeName]) === null || _a === void 0 ? void 0 : _a.call(stepValidators, route)) !== null && _b !== void 0 ? _b : null;
    }
    return null;
};
const getCurrentFlowStepCompletionAction = (route, { completionActions }) => {
    var _a;
    // Return results of validator if present. Otherwise, pass validation.
    const routeName = route.name;
    if (routeName) {
        return (_a = completionActions === null || completionActions === void 0 ? void 0 : completionActions[routeName]) !== null && _a !== void 0 ? _a : null;
    }
    return null;
};
const flowPerformCompletionAction = async (route, flowConfig, stepMap) => {
    const action = getCurrentFlowStepCompletionAction(route, flowConfig);
    const genericError = `Something went wrong. ${errorSuffix}`;
    const { setNextLoading, } = flowConfig;
    if (!setNextLoading || !action) {
        reportException('Completion action failed due to missing data.');
        throw Error(genericError);
    }
    setNextLoading(true);
    try {
        const actionResponse = await action(route);
        if (actionResponse === false) {
            setNextLoading(false);
            return;
        }
        const nextRoute = typeof actionResponse === 'object'
            ? actionResponse
            : getBackOrNextFlowStepRoute({
                direction: 'next',
                flowConfig,
                route,
                stepMap,
            });
        if (nextRoute)
            getRouter().push(keepParams(keepQuery(nextRoute)));
    }
    catch (error) {
        // Errors thrown in completion actions will be shown to the user.
        toastError(error);
    }
    setNextLoading(false);
};
const getCurrentFlowStepTitleAction = (route, { titleActions }) => {
    var _a;
    const routeName = route.name;
    if (routeName) {
        return (_a = titleActions === null || titleActions === void 0 ? void 0 : titleActions[routeName]) !== null && _a !== void 0 ? _a : null;
    }
    return null;
};
const getCurrentFlowStepTitle = (route, flowConfig) => {
    const action = getCurrentFlowStepTitleAction(route, flowConfig);
    if (!action)
        return;
    const title = action(route);
    return title;
};
const getFlowStepTitleHeaderComponent = (route, flowConfig, stepMap) => {
    var _a;
    const currentStep = getFlowStepFromRoute(flowConfig, route, stepMap);
    return (_a = currentStep === null || currentStep === void 0 ? void 0 : currentStep.titleHeaderComponent) !== null && _a !== void 0 ? _a : null;
};
const getCurrentFlowProgressSteps = (route, flowConfig, stepMap) => {
    var _a;
    const stepIndex = getRootLevelStepIndex((_a = route.name) !== null && _a !== void 0 ? _a : null, stepMap);
    return [...stepMap.values()].map((flowStep, index) => {
        var _a, _b, _c;
        let status = ProgressStepStatus.INCOMPLETE;
        if (index < stepIndex) {
            status = ProgressStepStatus.COMPLETE;
        }
        else if (index === stepIndex) {
            status = ProgressStepStatus.IN_PROGRESS;
        }
        const paramID = (_a = route.params[flowStep.associatedParam]) !== null && _a !== void 0 ? _a : '';
        const to = getRouteFromFlowStep(flowConfig, flowStep, paramID);
        const allowNavigation = (_c = (_b = flowStep.getAllowNavigation) === null || _b === void 0 ? void 0 : _b.call(flowStep)) !== null && _c !== void 0 ? _c : true;
        return {
            title: flowStep.title,
            status,
            to,
            navigationToDisabled: !allowNavigation,
        };
    });
};
/**
 * Returns objects with information on all fetchable params, including those
 * that currently do not need to be fetched. If fetch is needed,
 * `needsInitialFetch` or `needsRefetch` will be true.
 *
 * Fetch is needed if:
 *   - No data fetched yet during this session, or
 *   - Data is stale
 *
 * Example return value - Itinerary needs fetch, waypoint does not:
 * ```
 * {
 *   itineraryID: {
 *     associatedID: 'abc123',
 *     param: 'itineraryID',
 *     fetchedAt: 1623930000000,
 *     needsRefetch: true,
 *   },
 *   waypointID: {
 *     associatedID: 'def456',
 *     param: 'itineraryID',
 *   },
 * }
 * ```
 */
const getFetchRequirements = (stepParams, flowConfig) => {
    const { getDataFetched } = flowConfig;
    if (!getDataFetched)
        throw toastError('Missing data fetched getter.');
    const fetchers = flowConfig.dataFetchers;
    if (!fetchers)
        throw toastError('Missing data fetchers.');
    const fetchRequirementsObj = objectMap(stepParams, (associatedID, param) => {
        var _a;
        const baseRequirement = {
            param,
            associatedID,
        };
        if (!associatedID)
            return baseRequirement;
        const fetcher = fetchers[param];
        if (!fetcher)
            return baseRequirement;
        const dataFetchedByParam = getDataFetched();
        const dataFetchedByAssociatedID = (_a = dataFetchedByParam[param]) !== null && _a !== void 0 ? _a : {};
        const dataFetchedObj = dataFetchedByAssociatedID[associatedID];
        // Not fetched yet during this session, fetch now.
        if (!dataFetchedObj)
            return { ...baseRequirement, needsInitialFetch: true };
        const { staleMinutes } = fetcher;
        // Refetching is only enabled if `staleMinutes` is defined
        if (!staleMinutes)
            return baseRequirement;
        const { fetchedAt } = dataFetchedObj;
        const dataExpiry = fetchedAt + (staleMinutes * MINUTE);
        const dataIsStale = Date.now() > dataExpiry;
        if (dataIsStale)
            return { ...baseRequirement, fetchedAt, needsRefetch: true };
        return baseRequirement;
    });
    const fetchRequirements = Object.values(fetchRequirementsObj);
    return fetchRequirements;
};
const executeFetches = async (fetchRequirements, flowConfig) => {
    const fetchers = flowConfig.dataFetchers;
    if (!fetchers)
        throw toastError('Missing data fetchers.');
    const fetchPromises = fetchRequirements.map(async (requirement) => {
        const { param, associatedID, fetchedAt, needsRefetch, needsInitialFetch } = requirement;
        const needsFetch = needsRefetch || needsInitialFetch;
        if (!needsFetch)
            return;
        const fetcher = fetchers[param];
        if (!fetcher)
            throw Error('Unexpected missing fetcher');
        const { fetchAction, loadingMessageGetter } = fetcher;
        const loadingMessage = loadingMessageGetter(associatedID);
        const { getDataFetched, setDataFetched, setFlowLoading, setFlowLoadingError } = flowConfig;
        if (!getDataFetched || !setFlowLoading || !setDataFetched || !setFlowLoadingError) {
            throw Error('Unexpected missing data fetching getter or setter');
        }
        setFlowLoading(loadingMessage);
        try {
            if (getIsDebugMode()) {
                const fetchType = needsRefetch ? 'refetch' : 'initial';
                console.info(`🌍 Fetching ${param} ${associatedID} (${fetchType})`);
            }
            await fetchAction(associatedID);
            const oldDataFetched = getDataFetched();
            const newFetchInfo = { fetchedAt: Date.now() };
            const newDataFetched = update(oldDataFetched, {
                [param]: {
                    [associatedID]: { $set: newFetchInfo },
                },
            });
            setDataFetched(newDataFetched);
        }
        catch (error) {
            const message = `There was a problem loading data for ${param} ${associatedID}.`;
            if (needsRefetch) {
                const timeAgo = dayjs().to(fetchedAt);
                const refetchFailureMessage = `${message} Data from ${timeAgo} will be used.`;
                // Log custom message to console for debugging purposes (fullstory, etc.)
                console.error(refetchFailureMessage);
                reportException(error);
            }
            else {
                reportException(error);
                const globalFailureMessage = `${message} ${errorSuffixNurse}`;
                setFlowLoadingError(globalFailureMessage);
            }
        }
        setFlowLoading(false);
    });
    // Perform fetch actions
    await Promise.all(fetchPromises);
};
const maybeFetchFlowData = async (route, paramsEnum, flowConfig) => {
    if (getIsDebugMode())
        console.info('🌍 maybeFetchFlowData started');
    if (!paramsEnum)
        throw Error('Missing params enum.');
    const stepParamsWithUndefined = filterObjectByEnum(route.params, paramsEnum);
    /** Ex: `{ itineraryID: 'abc123', waypointID: 'def456', procedureID: 'xyz789' }` */
    const stepParams = objectFilter(stepParamsWithUndefined, (value) => !!value);
    const fetchers = flowConfig.dataFetchers;
    if (!fetchers)
        throw toastError('Missing data fetchers.');
    if (getIsDebugMode()) {
        const flowFetchables = Object.keys(fetchers).join(', ');
        console.info(`🌍 Fetchable params for entire flow: ${flowFetchables}`);
    }
    const fetchRequirements = getFetchRequirements(stepParams, flowConfig);
    if (getIsDebugMode()) {
        const currentStepFetchables = fetchRequirements.map((requirement) => requirement.param).join(', ');
        console.info(`🌍 Fetchable params on current step: ${currentStepFetchables}`);
    }
    await executeFetches(fetchRequirements, flowConfig);
    if (getIsDebugMode())
        console.info('🌍 maybeFetchFlowData finished');
};
const initFlowHelpers = (flowConfig, stepMap) => ({
    /**
     * Gets the step for navigation to `back` or `next`.
     *
     * - The very first root step has no `back` step.
     *   - The `SomeFlow.vue` file can handle this case by returning to a particular
     *     page if this function returns null.
     * - The very last root step has no `next` step.
     *   - The `SomeFlow.vue` file can handle this case by returning to a particular
     *     page if this function returns null.
     * - Otherwise, the `next` step returns as follows:
     *   - The next step in current set of (sub)steps, or, if there is none,
     *   - The parent step in the hierarchy,
     * - Otherwise, the `back` step returns as follows:
     *   - The last step in current set of (sub)steps, or, if there is none,
     *   - The parent step in the hierarchy
     */
    getBackOrNextStep: (route, direction) => getBackOrNextFlowStep({ direction, flowConfig, route, stepMap }),
    /**
     * Gets the route associated with the steps for navigation to `back` or `next`.
     *
     * - The very first root step has no `back` step.
     *   - The `SomeFlow.vue` file can handle this case by returning to a particular
     *     page if this function returns null.
     * - The very last root step has no `next` step.
     *   - The `SomeFlow.vue` file can handle this case by returning to a particular
     *     page if this function returns null.
     * - Otherwise, the `next` step returns as follows:
     *   - The next step in current set of (sub)steps, or, if there is none,
     *   - The parent step in the hierarchy,
     * - Otherwise, the `back` step returns as follows:
     *   - The last step in current set of (sub)steps, or, if there is none,
     *   - The parent step in the hierarchy
     */
    getBackOrNextStepRoute: (route, direction) => getBackOrNextFlowStepRoute({ direction, flowConfig, route, stepMap }),
    /**
     * Given a step, return a back component to render as the back button.
     */
    getCurrentStepBackHeaderComponent: (route) => getFlowStepBackHeaderComponent(route, flowConfig, stepMap),
    getCurrentStepCompletionAction: (route) => getCurrentFlowStepCompletionAction(route, flowConfig),
    /**
     * Get the current step map in the hierarchy.
     *
     * - If the user is on a root step, this is the root step map.
     * - Otherwise, this is the subStep map of the current hierarchy. i.e., In the
     *   patient visit flow, if the user is on a procedure step, it is only the
     *   steps specific to that procedure.
     */
    getCurrentSubSteps: (route) => getCurrentFlowSubSteps({ flowConfig, route, stepMap }),
    getCurrentStepTitle: (route) => getCurrentFlowStepTitle(route, flowConfig),
    /**
     * Given a step, return a title component to render as the title.
     */
    getCurrentStepTitleHeaderComponent: (route) => getFlowStepTitleHeaderComponent(route, flowConfig, stepMap),
    /**
     * Returns a flattened list of all the possible steps.
     * This can be used to easily find a specific step in the hierarchy, without
     * needing to work with nested data.
     */
    getFlattenedSteps: () => getFlattenedFlowSteps(stepMap),
    getCurrentStepValidationFailures: (route) => getCurrentFlowStepValidationFailures(route, flowConfig),
    /**
     * Generates a route from available information (rootParam, associatedParam,
     * parentAssociatedParam) In a deeply nested structure there could be params
     * missing in the object returned from this function, but any params that are
     * already in the URL will remain upon navigation.
     */
    getRouteFromStep: (step, rootID) => getRouteFromFlowStep(flowConfig, step, rootID),
    /**
     * Given a route, return the appropriate step. This is typically used to get
     * the current step, but is also used in conjunction with `prevRoute` to get
     * the step for the route the user visited last.
     */
    getStepFromRoute: (route) => getFlowStepFromRoute(flowConfig, route, stepMap),
    /** Given a routeName and associated ID, find a specific step */
    getStepByRouteNameAndAssociatedID: (routeName, associatedID) => getFlowStepByRouteNameAndAssociatedID(routeName, associatedID, getFlattenedFlowSteps(stepMap)),
    /**
     * Given a step, return a secondary component to render next to the confirmation button.
     */
    getCurrentStepSecondaryHeaderComponent: (route) => getFlowStepSecondaryHeaderComponent(route, flowConfig, stepMap),
    /**
     * Given a step, return a tertiary component to render next to the secondary/confirmation button.
     */
    getCurrentStepTertiaryHeaderComponent: (route) => getFlowStepTertiaryHeaderComponent(route, flowConfig, stepMap),
    /**
     * For flows set up with advanced data fetching, attempts to fetch needed
     * data.
     * - Flow loading screen may be triggered if set up in-app via
     *   `getFlowLoading`
     * - In case of failure during initial data loads, the flow global error
     *   screen will be triggered if set up in-app via `getFlowLoadingError`
     * - In case of failure during stale data reloads, a toast will be displayed
     *   to the user, but they will be allowed to continue to use the app with
     *   stale data.
     */
    maybeFetchFlowData: (route) => maybeFetchFlowData(route, flowConfig.paramsEnum, flowConfig),
    performCompletionAction: (route) => flowPerformCompletionAction(route, flowConfig, stepMap),
    getProgressSteps: (route) => getCurrentFlowProgressSteps(route, flowConfig, stepMap),
});

export { addAssociatedIDs, flowPerformCompletionAction, getBackOrNextFlowStep, getBackOrNextFlowStepRoute, getCurrentFlowProgressSteps, getCurrentFlowStepCompletionAction, getCurrentFlowStepTitle, getCurrentFlowStepTitleAction, getCurrentFlowStepValidationFailures, getCurrentFlowSubSteps, getFlattenedFlowSteps, getFlowStepBackHeaderComponent, getFlowStepByRouteNameAndAssociatedID, getFlowStepFromRoute, getFlowStepSecondaryHeaderComponent, getFlowStepTertiaryHeaderComponent, getFlowStepTitleHeaderComponent, getParentFlowStep, getRootLevelStepIndex, getRouteFromFlowStep, initFlowHelpers, maybeFetchFlowData };
